import { faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import css from './product-list.module.css';
import useProductList, { IEmptyProduct } from '../hooks/use-product-list';
import { useParams } from 'react-router-dom';
import { FormMode } from '../../../../../enums/form-mode';
import {
  FormComponent,
  FormComponentProps,
} from '../../../../../components/form';

export default function ProductList(props: {
  products: {
    field: FormComponent[];
    name: string | ((index: number) => string);
    title: string;
  }[];
  emptyProduct: IEmptyProduct;
}): FormComponent {
  const [product, warehouse] = props.products;

  const { title, name, field } = product;
  return ({ ...rest }) => (
    <ProductListComponent
      field={field}
      name={name}
      title={title}
      warehouse_data={warehouse}
      emptyProduct={props.emptyProduct}
      {...rest}
    />
  );
}

export function ProductListComponent({
  field,
  name,
  title,
  parentName,
  formValues,
  warehouse_data,
  emptyProduct,
  ...rest
}: FormComponentProps & {
  field: FormComponent[];
  name: string | ((index: number) => string);
  title: string;
  formValues?: object;
  emptyProduct: IEmptyProduct;
  warehouse_data: {
    field: FormComponent[];
    name: string | ((index: number) => string);
    title: string;
  };
}) {
  const { t } = useTranslation();
  const tp = (key: string) =>
    t('pages.livestock.animals_and_events.animals.'.concat(key));
  const { mode } = useParams<{
    mode: string;
  }>();

  const real_name = typeof name === 'string' ? name : name(0);
  const { state, removeField, addField } = useProductList({
    field,
    name: real_name,
    formValues,
    emptyProduct,
  });

  return (
    <div className={css['fields']}>
      <div className={'flex items-center justify-between'}>
        {title && <h3>{title}</h3>}
      </div>

      {Object.keys(state.fields).map((key, index) => (
        <fieldset key={real_name.concat('-', key)} className={css['field']}>
          <div className={css['body']}>
            {mode !== FormMode.view && (
              <div className={css['remove']}>
                {index !== 0 && index + 1 === state.count && (
                  <button
                    type="button"
                    onClick={() => removeField(key, real_name)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            )}
            <div className={css['components']}>
              {state.fields[key].map((Child, index) => {
                return (
                  <Child
                    key={real_name.concat('-', key, '-', index.toString())}
                    parentName={(parentName
                      ? parentName.concat('.')
                      : ''
                    ).concat(real_name, '.', key)}
                    {...rest}
                  />
                );
              })}
              <WareHouseListComponent
                emptyProduct={emptyProduct}
                {...warehouse_data}
                index={index}
                {...rest}
              />
            </div>
          </div>
        </fieldset>
      ))}

      <div className="flex justify-end">
        {mode !== FormMode.view && (
          <div className={css['add']}>
            <button
              className="add-werhouse"
              type="button"
              onClick={() => addField({ key: real_name })}
            >
              <FontAwesomeIcon icon={faPlus} /> {tp('add_product')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function WareHouseListComponent({
  children,
  field,
  name,
  title,
  parentName,
  formValues,
  index,
  emptyProduct,
  ...rest
}: PropsWithChildren<
  FormComponentProps & {
    field: FormComponent[];
    name: string | ((index: number) => string);
    title: string;
    formValues?: object;
    emptyProduct: IEmptyProduct;
    index: number;
  }
>) {
  const { t } = useTranslation();
  const tp = (key: string) =>
    t('pages.livestock.animals_and_events.animals.'.concat(key));
  const { mode } = useParams<{
    mode: string;
  }>();

  const real_name = typeof name === 'string' ? name : name(index);
  const { state, removeField, addField } = useProductList({
    field,
    name: real_name,
    formValues,
    emptyProduct,
  });

  return (
    <div className={`${css['fields']} child_product_list`}>
      {title && <h3>{title}</h3>}
      {Object.keys(state.fields).map((key, index) => (
        <fieldset key={real_name.concat('-', key)} className={css['field']}>
          <div className={css['body']}>
            {mode !== FormMode.view && (
              <div className={css['remove']}>
                {index !== 0 && index + 1 === state.count && (
                  <button
                    type="button"
                    onClick={() => removeField(key, real_name)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            )}
            <div className={css['components']}>
              {state.fields[key].map((Child, index) => {
                return (
                  <Child
                    key={real_name.concat('-', key, '-', index.toString())}
                    parentName={(parentName
                      ? parentName.concat('.')
                      : ''
                    ).concat(real_name, '.', key)}
                    {...rest}
                  />
                );
              })}
            </div>
          </div>
        </fieldset>
      ))}
      {mode !== FormMode.view && (
        <div className={'flex justify-end items-center'}>
          <div className={css['add']}>
            <button
              className="add-werhouse"
              type="button"
              onClick={() => addField({ key: real_name, productIdx: index })}
            >
              <FontAwesomeIcon icon={faPlus} /> {tp('add_warehouse')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
